import { MAX_BARCODE_LENGTH } from '../barcodeRange'

// 各要素は「1文字ぶんのバーコード見た目」を表す疑似パターン。
// 配列の数値は左から順に「黒棒の幅, 白余白の幅, 黒棒の幅...」を並べている。
//
// 例: [3, 1, 2, 1, 3, 1, 2, 1, 2]
// - 3px の黒棒
// - 1px の余白
// - 2px の黒棒
// - 1px の余白
// - ...
//
// このプレビューは実在のバーコード規格をエンコードしているわけではなく、
// 「各桁が縦棒で構成される」「桁ごとに少しずつ見た目が違う」という状態を
// 視覚的に伝えるためのダミーパターンを使っている。
//
// 各パターンの合計幅は 16px に揃えてあり、1文字ぶんの表示幅と一致する。
// そのため、桁ごとに描画しても末尾に余白が余ったり、桁境界がずれたりしない。
// パターン数より表示桁数が多い場合は、配列を先頭から循環利用する。
const BAR_PATTERNS: number[][] = [
  [3, 1, 2, 1, 3, 1, 2, 1, 2],
  [2, 1, 3, 1, 2, 1, 4, 1, 1],
  [1, 2, 3, 1, 2, 1, 3, 1, 2],
  [2, 1, 2, 2, 3, 1, 2, 1, 2],
  [1, 1, 4, 1, 2, 1, 3, 2, 1],
  [3, 2, 2, 1, 2, 1, 2, 2, 1],
  [2, 1, 2, 1, 4, 1, 2, 1, 2],
  [1, 2, 2, 1, 3, 1, 4, 1, 1],
  [3, 1, 1, 2, 3, 1, 2, 1, 2],
  [2, 2, 3, 1, 2, 1, 2, 1, 2],
  [1, 1, 2, 2, 4, 1, 2, 1, 2],
  [3, 1, 2, 1, 2, 1, 3, 2, 1],
  [2, 1, 3, 2, 2, 1, 2, 1, 2],
]

export const BARCODE_PREVIEW_BAR_HEIGHT = 120

const BARCODE_PREVIEW_CHAR_SECTION_WIDTH = 16
const MIN_BARCODE_PREVIEW_LENGTH = 13
const POSITION_CHARS = '1234567890abcdefghijklmnopqrstuvwxyz'

export type BarcodePreviewBar = {
  x: number
  width: number
  inRange: boolean
}

export type BarcodePreviewPosition = {
  value: number
  label: string
  inRange: boolean
}

type BuildBarcodePreviewModelParams = {
  startPosition: number
  endPosition: number
}

type BarcodePreviewModel = {
  bars: BarcodePreviewBar[]
  positions: BarcodePreviewPosition[]
  svgWidth: number
}

export const getBarcodePreviewPositionLabel = (position: number): string =>
  POSITION_CHARS[(position - 1) % POSITION_CHARS.length] ?? '?'

const isPositionInRange = (
  position: number,
  startPosition: number,
  endPosition: number,
) => position >= startPosition && position <= endPosition

// 桁数がパターン定義数を超えたら、先頭から同じ見た目を繰り返す。
const getPattern = (index: number): number[] =>
  BAR_PATTERNS[index % BAR_PATTERNS.length] ?? []

// ラベル表示に必要な情報を 1 桁ぶんに正規化する。
const buildPosition = (
  position: number,
  startPosition: number,
  endPosition: number,
): BarcodePreviewPosition => ({
  value: position,
  label: getBarcodePreviewPositionLabel(position),
  inRange: isPositionInRange(position, startPosition, endPosition),
})

const buildBarsInSection = ({
  inRange,
  pattern,
  sectionX,
}: {
  inRange: boolean
  pattern: number[]
  sectionX: number
}): BarcodePreviewBar[] => {
  const bars: BarcodePreviewBar[] = []
  let currentX = sectionX

  // pattern は「黒棒, 余白, 黒棒...」の交互表現なので、
  // 偶数 index だけを実際の <rect> として扱う。
  pattern.forEach((width, patternIndex) => {
    if (patternIndex % 2 === 0) {
      bars.push({ x: currentX, width, inRange })
    }

    currentX += width
  })

  return bars
}

export const buildBarcodePreviewModel = ({
  startPosition,
  endPosition,
}: BuildBarcodePreviewModelParams): BarcodePreviewModel => {
  // フォームが不正値を含んでいても、仕様上限を超えるプレビューは描画しない。
  const totalLength = Math.min(
    MAX_BARCODE_LENGTH,
    Math.max(MIN_BARCODE_PREVIEW_LENGTH, endPosition),
  )

  // 先に「何桁表示するか」「各桁が選択範囲内か」を確定させる。
  const positions = Array.from({ length: totalLength }, (_, index) =>
    buildPosition(index + 1, startPosition, endPosition),
  )

  // 各桁の開始 x 座標だけを計算し、1 桁ぶんの棒配列へ展開する。
  const bars = positions.flatMap((position, index) =>
    buildBarsInSection({
      inRange: position.inRange,
      pattern: getPattern(index),
      sectionX: index * BARCODE_PREVIEW_CHAR_SECTION_WIDTH,
    }),
  )

  return {
    bars,
    positions,
    svgWidth: totalLength * BARCODE_PREVIEW_CHAR_SECTION_WIDTH,
  }
}
